import type { MarketLocale } from '../../apps.js';

/**
 * A market's synthetic-profile vocabulary: the names, handle parts and bio
 * material that make a generated profile read like a real member of that
 * app. One pack per `MarketLocale`; the generators are locale-agnostic and
 * read everything market-specific from here.
 */

/** Birth cohort a first name was popular in. */
export type Era = 'pre95' | '95-99' | '00-04' | '05-09' | 'post09';

/** `weight` is relative popularity within its list (any positive scale). */
export interface WeightedName {
  name: string;
  weight: number;
  eras: Era[];
}

export interface Weighted {
  name: string;
  weight: number;
}

export type ZodiacSign =
  | 'aries' | 'taurus' | 'gemini' | 'cancer' | 'leo' | 'virgo'
  | 'libra' | 'scorpio' | 'sagittarius' | 'capricorn' | 'aquarius' | 'pisces';

export type BioTopic = 'zodiac' | 'team' | 'school' | 'job' | 'hobby' | 'lyric' | 'oneLiner' | 'height' | 'pet';

export type OneLinerRegister = 'funny' | 'catchy' | 'cringe' | 'sincere';

export interface BioBanks {
  /**
   * Whole-bio lines per register. Slots allowed: {hobby} {city} {trait}
   * {team} {zodiac} {school} {job}. No emoji (the engine adds them).
   */
  oneLiners: Record<OneLinerRegister, string[]>;
  hobbies: string[];
  traits: string[];
  cities: Weighted[];
  teams: string[];
  schools: string[];
  jobs: string[];
  /** Short song lines, sayings or quotes people put in bios. */
  lyricsOrQuotes: string[];
  /** Pet mentions, e.g. "2 kedi annesi", "mon chat Pixel". */
  pets: string[];
  /** Localized sign name, as people write it in a bio. */
  zodiac: Record<ZodiacSign, string>;
  /**
   * How each topic is phrased when it is one fragment of a bio. Each template
   * must contain that topic's slot: {zodiac} {team} {school} {job} {hobby}
   * {lyric} {oneLiner} {height} {pet}; it may also use {city} {trait}.
   */
  topicTemplates: Record<BioTopic, string[]>;
  /** Height as people write it in this market (e.g. 1.78 / 1m78 / 1,78). */
  formatHeight: (cm: number) => string;
  emojis: { general: string[]; female: string[]; male: string[] };
  /** Typing habits applied per bio so they do not all read as proofread. */
  imperfections: {
    substitutions: [RegExp, string][];
    lowercaseRate: number;
    dropDiacriticsRate: number;
    noFinalPunctRate: number;
  };
  /** Relative topic weights per gender; a missing topic weighs 1. */
  genderTopicBias: { female: Partial<Record<BioTopic, number>>; male: Partial<Record<BioTopic, number>> };
}

export interface LocalePack {
  locale: MarketLocale;
  names: { female: WeightedName[]; male: WeightedName[]; surnames: Weighted[] };
  /** Strip to ASCII the way a user typing a handle would (ş→s, é→e, ü→ue). */
  asciiFold: (s: string) => string;
  lower: (s: string) => string;
  upper: (s: string) => string;
  handle: {
    /** Standalone local nick words ("tatlıcadı", "loulou", "schatzi"). */
    words: string[];
    adjectives: string[];
    nouns: string[];
    /** Plate / département / city codes people append (34, 75, HH). */
    placeCodes: Weighted[];
  };
  bio: BioBanks;
}

/** The cohort for a birth year. */
export function eraOf(birthYear: number): Era {
  if (birthYear < 1995) return 'pre95';
  if (birthYear < 2000) return '95-99';
  if (birthYear < 2005) return '00-04';
  if (birthYear < 2010) return '05-09';
  return 'post09';
}
